import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useRosterView } from '@/api/views';
import { useSimulationStore } from '@/state/simulationStore';
import { buildArtistLeaderboard } from '@/components/leaderboard/leaderboardData';
import LeaderboardTable from '@/components/leaderboard/LeaderboardTable';
import type { StudioId } from '@/domain/types';

/**
 * Fullscreen leaderboard display intended for a TV mounted in the studio. No
 * nav rail, no top bar — just the standings cycling in big type. Renders top 3
 * to keep the screen readable across the room.
 */
export default function LeaderboardTV() {
  const { studioId } = useParams<{ studioId: string }>();
  // Live reads (tv-ods.3): read all bookings/artists/studios from the roster view.
  const roster = useRosterView();
  const bookings = roster.data?.bookings ?? {};
  const artists = roster.data?.artists ?? {};
  const studios = roster.data?.studios ?? {};
  const clockIso = useSimulationStore((s) => s.clockIso);

  const studio = studioId ? studios[studioId as StudioId] : undefined;
  const rows = useMemo(
    () => buildArtistLeaderboard(bookings, artists, clockIso, studioId as StudioId | undefined),
    [bookings, artists, clockIso, studioId],
  );

  return (
    <main
      data-testid="leaderboard-tv"
      className="min-h-screen bg-ink-black text-cream flex flex-col px-12 py-10"
    >
      <header className="mb-8">
        <p className="font-inter text-gold-bright uppercase tracking-[0.4em] text-sm">
          {studio?.brand ?? 'Studio'} · {studio?.name ?? studioId}
        </p>
        <h1 className="heading-display text-6xl text-cream mt-3">In-chair rebooks</h1>
        <p className="font-serif text-cream-soft text-xl mt-2">
          This month&apos;s standings
        </p>
      </header>

      <div className="flex-1">
        <LeaderboardTable rows={rows} variant="tv" topN={3} />
      </div>

      <footer className="mt-8 font-inter text-cream-soft uppercase tracking-[0.3em] text-xs">
        Updates live · Press ESC to exit fullscreen
      </footer>
    </main>
  );
}
